import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  List as ListIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Visibility as ViewIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getRecords } from '../api/records';
import { updateSortOrder } from '../api/imageQuality';

export default function RecordsList() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [newSortOrder, setNewSortOrder] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    fetchRecords();
  }, [page, rowsPerPage]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      const offset = page * rowsPerPage;
      const result = await getRecords(rowsPerPage, offset);
      
      if (result.success) {
        setRecords(result.data);
        setTotalRecords(result.pagination.total);
      } else {
        setError(result.error || 'Failed to fetch records');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch records');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetail = (itemCode, imageType) => {
    navigate(`/records/${itemCode}/${imageType}`);
  };

  const handleOpenEditDialog = (record) => {
    setEditingRecord(record);
    setNewSortOrder(record.sort_order || '1');
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingRecord(null);
    setNewSortOrder('');
  };

  const handleSaveSortOrder = async () => {
    if (!editingRecord || !newSortOrder) return;

    const parsedNewSortOrder = parseInt(newSortOrder);
    if (isNaN(parsedNewSortOrder) || parsedNewSortOrder < 1) {
      setError('Sort order must be a positive number');
      return;
    }

    try {
      setError(null);
      const result = await updateSortOrder(
        editingRecord.item_code,
        editingRecord.image_type,
        editingRecord.sort_order || 1,
        parsedNewSortOrder
      );

      if (result.success) {
        setSnackbarMessage('Sort order updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleCloseEditDialog();
        // Refresh the list
        await fetchRecords();
      } else {
        throw new Error(result.error || 'Update failed');
      }
    } catch (err) {
      console.error('Update error:', err);
      const errorMessage = err.message || 'Failed to update sort order';
      setError(errorMessage);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Button
            color="inherit"
            startIcon={<BackIcon />}
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <ListIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Image Quality Records
          </Typography>
          <Button
            color="inherit"
            onClick={() => navigate('/image-quality')}
          >
            Upload New
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Uploaded Images History
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            View all analyzed product images and their quality scores.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <Paper elevation={3}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Item Code</strong></TableCell>
                      <TableCell><strong>Brand</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell align="center"><strong>Sort Order</strong></TableCell>
                      <TableCell align="center"><strong>Overall Score</strong></TableCell>
                      <TableCell align="center"><strong>Brightness</strong></TableCell>
                      <TableCell align="center"><strong>Sharpness</strong></TableCell>
                      <TableCell align="center"><strong>Contrast</strong></TableCell>
                      <TableCell align="center"><strong>Centered</strong></TableCell>
                      <TableCell align="center"><strong>Social Media</strong></TableCell>
                      <TableCell><strong>Analyzed At</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {records.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center">
                          <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
                            No records found. Upload your first image to get started.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      records.map((record) => (
                        <TableRow 
                          key={`${record.item_code}-${record.image_type}`}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleViewDetail(record.item_code, record.image_type)}
                        >
                          <TableCell>{record.item_code}</TableCell>
                          <TableCell>{record.brand_code}</TableCell>
                          <TableCell>
                            <Chip label={record.image_type} size="small" />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={record.sort_order || '1'} 
                              size="small"
                              color="default"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={record.overall_score?.toFixed(1) || 'N/A'} 
                              color={record.overall_score >= 75 ? 'success' : record.overall_score >= 50 ? 'warning' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">{record.brightness_score?.toFixed(1) || 'N/A'}</TableCell>
                          <TableCell align="center">{record.sharpness_score?.toFixed(1) || 'N/A'}</TableCell>
                          <TableCell align="center">{record.contrast_score?.toFixed(1) || 'N/A'}</TableCell>
                          <TableCell align="center">
                            {record.is_centered ? (
                              <CheckIcon color="success" />
                            ) : (
                              <WarningIcon color="warning" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {record.social_media_ready ? (
                              <Chip label="Ready" color="success" size="small" />
                            ) : (
                              <Chip label="Needs Work" color="warning" size="small" />
                            )}
                          </TableCell>
                          <TableCell>{formatDate(record.analyzed_at)}</TableCell>
                          <TableCell align="center">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetail(record.item_code, record.image_type);
                              }}
                              title="View Details"
                            >
                              <ViewIcon />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditDialog(record);
                              }}
                              title="Edit Sort Order"
                            >
                              <EditIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalRecords}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Paper>
          )}
        </Box>
      </Container>

      {/* Edit Sort Order Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Sort Order</DialogTitle>
        <DialogContent>
          {editingRecord && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Item Code:</strong> {editingRecord.item_code}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Image Type:</strong> {editingRecord.image_type}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Current Sort Order:</strong> {editingRecord.sort_order || '1'}
              </Typography>
              
              <TextField
                autoFocus
                margin="dense"
                label="New Sort Order"
                type="number"
                fullWidth
                variant="outlined"
                value={newSortOrder}
                onChange={(e) => setNewSortOrder(e.target.value)}
                inputProps={{ min: 1 }}
                helperText="Enter a positive number (1, 2, 3, etc.)"
                sx={{ mt: 2 }}
              />
              
              <Alert severity="info" sx={{ mt: 2 }}>
                The new sort order must not conflict with existing images of the same type for this item.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveSortOrder} 
            variant="contained" 
            color="primary"
            disabled={!newSortOrder || newSortOrder === (editingRecord?.sort_order?.toString() || '1')}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
